import React, { useState, useEffect, useRef } from 'react';
import { DndContext, closestCenter, DragOverlay, pointerWithin, rectIntersection } from '@dnd-kit/core';
import { Badge, Tag, Modal, message } from 'antd';
import { ClusterOutlined, HolderOutlined, PlusOutlined, MinusOutlined } from '@ant-design/icons';
import { permissionsFromRoles } from '../../../../core/utils/dataManipulating/organization';
import { suppressResizeObserverErrors } from '../../../../core/utils/errorSuppression';
import DraggableOrgNode from './DraggableOrgNode';
import DroppableOrgNode from './DroppableOrgNode';

const DraggableOrgTree = ({
  organizations = [],
  currentOrg,
  selectedOrg,
  onOrgSelect,
  onOrgMove,
  buildOrganizationTree,
  buildAntTree,
  isSearching = false,
}) => {
  const [activeId, setActiveId] = useState(null);
  const [draggedOrg, setDraggedOrg] = useState(null);
  const [expandedNodes, setExpandedNodes] = useState(new Set());

  // Initialize error suppression on component mount - apply globally and early
  const errorSuppressionRef = useRef(null);
  
  useEffect(() => {
    // Apply error suppression immediately and globally
    if (!errorSuppressionRef.current) {
      errorSuppressionRef.current = suppressResizeObserverErrors();
    }
    
    return () => {
      // Cleanup on unmount
      if (errorSuppressionRef.current) {
        errorSuppressionRef.current();
        errorSuppressionRef.current = null;
      }
    };
  }, []);

  // Additional error boundary for drag operations
  useEffect(() => {
    const handleError = (event) => {
      const errorMessage = event.error?.message || event.message || '';
      if (errorMessage.includes('ResizeObserver loop completed with undelivered notifications')) {
        event.preventDefault();
        event.stopPropagation();
        return false;
      }
    };

    // Add specific error handling during drag operations
    window.addEventListener('error', handleError, { capture: true });
    
    return () => {
      window.removeEventListener('error', handleError, { capture: true });
    };
  }, [activeId]); // Re-apply when drag state changes

  // Toggle expand/collapse state for a node
  const toggleNodeExpansion = (nodeId) => {
    setExpandedNodes(prev => {
      const newSet = new Set(prev);
      if (newSet.has(nodeId)) {
        newSet.delete(nodeId);
      } else {
        newSet.add(nodeId);
      }
      return newSet;
    });
  };

  // Helper function to validate move (simplified)
  const validateMove = (draggedOrg, targetParentId) => {
    if (!draggedOrg || !targetParentId) return { isValid: false };

    // Prevent dropping on self
    if (draggedOrg.id === targetParentId) {
      return { isValid: false, reason: "Cannot move organization into itself" };
    }

    // Prevent dropping on own children (circular dependency)
    const isDescendant = checkIfDescendant(draggedOrg, targetParentId, organizations);
    if (isDescendant) {
      return { isValid: false, reason: "Cannot move organization into its own child" };
    }

    // Check if target org exists
    const targetOrg = organizations.find(org => org.id === targetParentId);
    if (!targetOrg) return { isValid: false, reason: "Target organization not found" };

    return {
      isValid: true,
      targetOrg
    };
  };

  // Helper function to check if target is descendant of dragged org
  const checkIfDescendant = (draggedOrg, targetId, allOrgs) => {
    const findDescendants = (orgId) => {
      const children = allOrgs.filter(org => 
        org.parentOrg.length > 0 && 
        org.parentOrg[org.parentOrg.length - 1]?.id === orgId
      );
      
      let descendants = [...children];
      children.forEach(child => {
        descendants = [...descendants, ...findDescendants(child.id)];
      });
      
      return descendants;
    };

    const descendants = findDescendants(draggedOrg.id);
    return descendants.some(desc => desc.id === targetId);
  };

  const handleDragStart = (event) => {
    const { active } = event;
    setActiveId(active.id);
    
    const dragged = organizations.find(org => org.id === active.id);
    setDraggedOrg(dragged);
  };

  const handleDragEnd = (event) => {
    const { active, over } = event;

    setActiveId(null);
    setDraggedOrg(null);

    if (!over || active.id === over.id) {
      return;
    }

    const draggedOrgId = active.id;
    const targetParentId = over.id;
    const draggedOrg = organizations.find(org => org.id === draggedOrgId);
    
    const moveValidation = validateMove(draggedOrg, targetParentId);

    if (!moveValidation.isValid) {
      message.error(moveValidation.reason);
      return;
    }

    // Simple move - target org becomes parent
    onOrgMove(draggedOrgId, targetParentId);
  };

  // Build the tree structure for display
  const orgTree = buildOrganizationTree(organizations, currentOrg ? currentOrg.name : '');
  const antTree = buildAntTree(orgTree, '');

  // Render tree nodes recursively
  const renderTreeNode = (node, level = 0) => {
    const hasChildren = node.children && node.children.length > 0;
    const isExpanded = expandedNodes.has(node.id) || isSearching; // Auto-expand when searching
    
    return (
      <div key={node.id} style={{ marginLeft: level * 20 }}>
        <div style={{ display: 'flex', alignItems: 'center' }}>
          {/* Expand/Collapse Icon */}
          <div 
            style={{ 
              width: 20, 
              height: 20, 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'center',
              cursor: hasChildren ? 'pointer' : 'default',
              marginRight: 4,
              opacity: isSearching ? 0.5 : 1 // Dim icons when searching
            }}
            onClick={(e) => {
              e.stopPropagation();
              if (hasChildren && !isSearching) { // Disable toggle when searching
                toggleNodeExpansion(node.id);
              }
            }}
          >
            {hasChildren ? (
              isExpanded ? (
                <MinusOutlined style={{ fontSize: 12, color: '#666' }} />
              ) : (
                <PlusOutlined style={{ fontSize: 12, color: '#666' }} />
              )
            ) : (
              <span style={{ width: 12 }} /> // Spacer for alignment
            )}
          </div>
          
          {/* Organization Node */}
          <div style={{ flex: 1 }}>
            <DroppableOrgNode
              org={node}
              isSelected={selectedOrg && selectedOrg.id === node.id}
              onSelect={onOrgSelect}
              isDraggedOver={activeId && draggedOrg}
              dragValidation={activeId && draggedOrg ? validateMove(draggedOrg, node.id) : null}
            >
              <DraggableOrgNode
                org={node}
                isSelected={selectedOrg && selectedOrg.id === node.id}
                onSelect={onOrgSelect}
                isDragging={activeId === node.id}
              />
            </DroppableOrgNode>
          </div>
        </div>
        
        {/* Children - render if expanded or searching */}
        {hasChildren && isExpanded && (
          <div style={{ marginTop: 4 }}>
            {node.children.map(child => renderTreeNode(child, level + 1))}
          </div>
        )}
      </div>
    );
  };

  return (
    <DndContext
      collisionDetection={closestCenter}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
      autoScroll={false}
      measuring={{
        droppable: {
          strategy: 'whenDragging',
          frequency: 'optimized'
        },
        draggable: {
          strategy: 'whenDragging',
          frequency: 'optimized'
        }
      }}
      accessibility={{
        announcements: {
          onDragStart: () => '',
          onDragMove: () => '',
          onDragOver: () => '',
          onDragEnd: () => '',
          onDragCancel: () => ''
        }
      }}
    >
      <div className="draggable-org-tree">
        {antTree.map(node => renderTreeNode(node))}
      </div>
      
      <DragOverlay>
        {activeId && draggedOrg ? (
          <div style={{
            padding: '8px 12px',
            background: '#fff',
            border: '2px solid #1890ff',
            borderRadius: '6px',
            boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
            cursor: 'grabbing'
          }}>
            <Badge
              dot
              style={{
                backgroundColor: Object.keys(
                  permissionsFromRoles([draggedOrg.roles.find(role => role.isMaster)])
                ).includes('ENABLED') ? '#52c41a' : '#d9d9d9'
              }}
            />
            <span style={{ marginLeft: 8 }}>{draggedOrg.name}</span>
          </div>
        ) : null}
      </DragOverlay>
    </DndContext>
  );
};

export default DraggableOrgTree;
